import { ArrowUpRight } from 'lucide-react';
import { type FocusEvent, type MouseEvent, type PointerEvent, type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';
import { Badge } from '../../components/ui/Badge';
import { Popover } from '../../components/ui/Popover';
import { type GlossaryTerm, termHref } from './api';
import { kindMeta, statusNote } from './labels';

const OPEN_DELAY = 400;
const CLOSE_DELAY = 150;
/** Якщо щойно закрили іншу картку, наступна відкривається без затримки (як підказки). */
const WARM_WINDOW = 500;
let lastClosedAt = 0;

type Mode = 'closed' | 'preview' | 'pinned';

function insideLayer(card: HTMLElement | null, node: EventTarget | null): boolean {
  if (!card || !(node instanceof Node)) return false;
  const layer = card.closest('[data-ers-layer]') ?? card;
  return layer.contains(node);
}

/** Коротка картка поняття: термін, вид, визначення, синоніми, посилання на довідник. */
export function TermCard({ term, onFollow }: { term: GlossaryTerm; onFollow?: () => void }) {
  const note = statusNote(term.status);
  return (
    <div className="flex w-[min(20rem,calc(100vw-16px))] flex-col gap-2 p-3">
      <div className="flex items-start justify-between gap-3">
        <p className="min-w-0 text-body font-semibold break-words text-fg">{term.term}</p>
        <Badge className="mt-px">{kindMeta(term.kind).one}</Badge>
      </div>
      <p className="line-clamp-6 text-meta break-words text-fg-2">{term.definition || 'Визначення ще не додано.'}</p>
      {term.synonyms.length > 0 && <p className="text-caption break-words text-fg-3">Синоніми: {term.synonyms.join(', ')}</p>}
      {term.status === 'owner_decision_needed' && (
        <Badge tone="info" className="self-start">
          Потребує рішення власника
        </Badge>
      )}
      {note && <p className="text-caption text-fg-3">{note}</p>}
      <a
        href={termHref(term.term)}
        target="_blank"
        rel="noopener"
        onClick={onFollow}
        className="inline-flex items-center gap-1 self-start rounded-xs text-meta font-medium text-accent-fg underline-offset-2 hover:underline"
      >
        Докладніше
        <ArrowUpRight className="size-3.5" aria-hidden />
        <span className="sr-only">(відкриється в новій вкладці)</span>
      </a>
    </div>
  );
}

type TriggerProps = {
  term: GlossaryTerm;
  /** Доступна назва кнопки (для значка без тексту). */
  ariaLabel?: string;
  className?: string;
  children: ReactNode;
};

/**
 * Кнопка, що відкриває картку поняття.
 * Наведення мишею — картка із затримкою; фокус із клавіатури — одразу й без перенесення фокуса
 * (визначення зчитувач читає з aria-describedby). Клік, Enter чи дотик закріплюють картку й
 * переводять фокус на «Докладніше». Esc, клік поза карткою чи вихід фокуса з неї — закривають.
 */
export function TermTrigger({ term, ariaLabel, className, children }: TriggerProps) {
  const popId = useId();
  const descId = useId();
  const [mode, setModeState] = useState<Mode>('closed');
  const modeRef = useRef<Mode>('closed');
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const openTimer = useRef<number | undefined>(undefined);
  const closeTimer = useRef<number | undefined>(undefined);
  const muteFocus = useRef(false);

  const setMode = useCallback((next: Mode) => {
    if (next === 'closed' && modeRef.current !== 'closed') lastClosedAt = Date.now();
    modeRef.current = next;
    setModeState(next);
  }, []);

  const clearTimers = useCallback(() => {
    window.clearTimeout(openTimer.current);
    window.clearTimeout(closeTimer.current);
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  /** Закриття; якщо фокус був у закріпленій картці — повертаємо його на кнопку (без повторного відкриття). */
  const close = useCallback(() => {
    clearTimers();
    const restore = modeRef.current === 'pinned' && insideLayer(cardRef.current, document.activeElement);
    setMode('closed');
    if (restore && buttonRef.current) {
      muteFocus.current = true;
      buttonRef.current.focus({ preventScroll: true });
      muteFocus.current = false;
    }
  }, [clearTimers, setMode]);

  const openPreview = (el: HTMLButtonElement) => {
    setAnchor(el);
    setMode('preview');
  };

  const scheduleClose = () => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => {
      if (modeRef.current !== 'preview') return;
      const btn = buttonRef.current;
      // Кнопка у фокусі з клавіатури — картка лишається, доки фокус не піде.
      if (btn && document.activeElement === btn && btn.matches(':focus-visible')) return;
      setMode('closed');
    }, CLOSE_DELAY);
  };

  const onPointerEnter = (e: PointerEvent<HTMLButtonElement>) => {
    if (e.pointerType !== 'mouse') return;
    window.clearTimeout(closeTimer.current);
    if (modeRef.current !== 'closed') return;
    const el = e.currentTarget;
    window.clearTimeout(openTimer.current);
    openTimer.current = window.setTimeout(
      () => {
        if (modeRef.current === 'closed') openPreview(el);
      },
      Date.now() - lastClosedAt < WARM_WINDOW ? 0 : OPEN_DELAY,
    );
  };

  const onPointerLeave = (e: PointerEvent<HTMLElement>) => {
    if (e.pointerType !== 'mouse') return;
    window.clearTimeout(openTimer.current);
    if (modeRef.current === 'preview') scheduleClose();
  };

  const onFocus = (e: FocusEvent<HTMLButtonElement>) => {
    if (muteFocus.current || modeRef.current !== 'closed') return;
    if (!e.currentTarget.matches(':focus-visible')) return;
    clearTimers();
    openPreview(e.currentTarget);
  };

  const onBlur = (e: FocusEvent<HTMLButtonElement>) => {
    if (modeRef.current !== 'preview' || insideLayer(cardRef.current, e.relatedTarget)) return;
    clearTimers();
    setMode('closed');
  };

  const onClick = (e: MouseEvent<HTMLButtonElement>) => {
    // Не активуємо рядок чи картку, всередині якої стоїть термін.
    e.stopPropagation();
    clearTimers();
    if (modeRef.current === 'pinned') {
      close();
      return;
    }
    setAnchor(e.currentTarget);
    setMode('pinned');
  };

  // Закріплена картка: фокус на «Докладніше», щоб до посилання можна було дістатися з клавіатури.
  useEffect(() => {
    if (mode !== 'pinned') return;
    const t = window.setTimeout(() => {
      const card = cardRef.current;
      if (!card || insideLayer(card, document.activeElement)) return;
      card.querySelector<HTMLElement>('a[href]')?.focus({ preventScroll: true });
    }, 0);
    return () => window.clearTimeout(t);
  }, [mode]);

  const onCardBlur = (e: FocusEvent<HTMLDivElement>) => {
    const next = e.relatedTarget;
    // Фокус пішов у нікуди (інша вкладка, вікно) — лишаємо картку.
    if (!(next instanceof Node)) return;
    if (insideLayer(cardRef.current, next) || buttonRef.current?.contains(next)) return;
    clearTimers();
    setMode('closed');
  };

  const open = mode !== 'closed' && anchor !== null;
  const description = [term.definition, statusNote(term.status)].filter(Boolean).join(' ') || term.term;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={ariaLabel}
        aria-describedby={descId}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popId : undefined}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        onFocus={onFocus}
        onBlur={onBlur}
        onClick={onClick}
        className={className}
      >
        {children}
      </button>
      <span id={descId} hidden>
        {description}
      </span>
      <Popover
        open={open}
        onClose={close}
        anchor={anchor}
        placement="bottom-start"
        initialFocus="none"
        restoreFocus={false}
        id={popId}
        aria-label={`Поняття «${term.term}»`}
      >
        <div
          ref={cardRef}
          onPointerEnter={(e) => {
            if (e.pointerType === 'mouse') window.clearTimeout(closeTimer.current);
          }}
          onPointerLeave={onPointerLeave}
          onBlur={onCardBlur}
        >
          <TermCard
            term={term}
            onFollow={() => {
              clearTimers();
              setMode('closed');
            }}
          />
        </div>
      </Popover>
    </>
  );
}
